import { useSetAtom } from 'jotai';
import { useCallback,useState } from 'react';

import type { IInventoryItem } from '@ekhodealer/ekho-common';

import { clearInventorySelectionAtom } from '../../atoms/inventorySelectionAtoms';
import { useEnableOnlineSalesSelection } from '../../hooks/useEnableOnlineSalesSelection';

interface UseEnableOnlineSalesActionsParams {
	selectedIds: Set<string>;
}

/**
 * Custom hook that provides action handlers for enabling online sales on inventory items
 * Manages enable online sales modal state and handles confirmation flow
 */
export const useEnableOnlineSalesActions = ({ selectedIds }: UseEnableOnlineSalesActionsParams) => {
	const { enableItemsByIds, isPending: isEnablingOnlineSales } = useEnableOnlineSalesSelection();
	const clearSelection = useSetAtom(clearInventorySelectionAtom);

	// Enable online sales modal state
	const [isEnableOnlineSalesModalOpen, setIsEnableOnlineSalesModalOpen] = useState(false);
	const [enableInitiatedCount, setEnableInitiatedCount] = useState(0);

	// Handle opening enable online sales modal
	const handleOpenEnableOnlineSalesModal = useCallback(() => {
		setEnableInitiatedCount(0); // Reset for fresh modal
		setIsEnableOnlineSalesModalOpen(true);
	}, []);

	// Handle enable online sales confirmation
	const handleEnableOnlineSalesConfirm = useCallback(
		async (readyItems: IInventoryItem[]) => {
			// Use provided items directly (already fetched in modal) to avoid duplicate fetches
			const count = await enableItemsByIds(selectedIds, readyItems);
			setEnableInitiatedCount(count);
			clearSelection();
		},
		[enableItemsByIds, selectedIds, clearSelection],
	);

	// Handle enable online sales cancel
	const handleEnableOnlineSalesCancel = useCallback(() => {
		setIsEnableOnlineSalesModalOpen(false);
	}, []);

	return {
		isEnableOnlineSalesModalOpen,
		setIsEnableOnlineSalesModalOpen,
		enableInitiatedCount,
		isEnablingOnlineSales,
		handleOpenEnableOnlineSalesModal,
		handleEnableOnlineSalesConfirm,
		handleEnableOnlineSalesCancel,
	};
};
